
import React, { useState } from 'react';
import { Mail, Send, Eye, Monitor, Smartphone, CheckCircle2, Layout, UserCheck, ShieldCheck } from 'lucide-react';
import { Card, Badge } from './SharedComponents';
import { getManagerWelcomeEmail, getStaffWelcomeEmail } from './EmailTemplates';
import { Company, User, UserRole } from './types';

export const SettingsEmailTemplates = ({ company, user, isSuper }: { company?: Company, user: User, isSuper: boolean }) => {
  const [activePreview, setActivePreview] = useState<'manager' | 'staff'>('manager');
  const [viewType, setViewType] = useState<'desktop' | 'mobile'>('desktop');

  const defaultCompany: Company = company || {
    id: 'demo',
    name: 'Unidade Exemplo Sorocaba',
    plan: 'PRO',
    cnpj: '00.000.000/0001-99',
    managerName: 'Magnun Gestor',
    contactEmail: 'gestor@empresa.com.br',
    whatsapp: '(15) 99999-9999',
    address: 'Av. das Indústrias, 100',
    neighborhood: 'Centro',
    city: 'Sorocaba',
    state: 'SP',
    status: 'ACTIVE',
    vertical: 'EVENTS' as any,
    mrr: 797,
    receivable: 0,
    payable: 0,
    activeOrders: 0,
    usersCount: 1,
    lastSync: '',
    nfseEnabled: false,
    blocked: false,
    iaUsageCount: 0,
    equityValue: 0
  };

  const effectiveCompany: Company = company || defaultCompany;

  const html = activePreview === 'manager' 
    ? getManagerWelcomeEmail(effectiveCompany, user)
    : getStaffWelcomeEmail(effectiveCompany.name, { ...user, role: UserRole.MONTAGEM }, effectiveCompany.logo);

  return (
    <div className="space-y-8 animate-in slide-in-from-bottom-2 duration-500">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h3 className={`text-2xl font-black uppercase tracking-tight flex items-center gap-3 ${isSuper ? 'text-white' : 'text-secondary'}`}>
            <Mail size={32} className="text-primary" /> Automações de Onboarding
          </h3>
          <p className={`text-[10px] font-bold uppercase tracking-widest mt-1 ${isSuper ? 'text-white/40' : 'text-gray-400'}`}>
            Configure e valide o visual das comunicações automáticas
          </p>
        </div>
        <div className="flex bg-gray-100 p-1 rounded-xl gap-1">
           <button onClick={() => setViewType('desktop')} className={`p-2 rounded-lg transition-all ${viewType === 'desktop' ? 'bg-white text-primary shadow-sm' : 'text-gray-400'}`}><Monitor size={18} /></button>
           <button onClick={() => setViewType('mobile')} className={`p-2 rounded-lg transition-all ${viewType === 'mobile' ? 'bg-white text-primary shadow-sm' : 'text-gray-400'}`}><Smartphone size={18} /></button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Seletor de Template */}
        <div className="lg:col-span-4 space-y-4">
           <TemplateCard 
            active={activePreview === 'manager'} 
            onClick={() => setActivePreview('manager')}
            icon={ShieldCheck}
            title="Boas-vindas Gestor"
            desc="Enviado após provisionamento de unidade."
           />
           <TemplateCard 
            active={activePreview === 'staff'} 
            onClick={() => setActivePreview('staff')}
            icon={UserCheck}
            title="Boas-vindas Equipe"
            desc="Enviado após cadastro de novo usuário."
           />
           
           <div className={`p-6 rounded-[32px] border-2 border-dashed ${isSuper ? 'border-white/10 text-white/40' : 'border-gray-200 text-gray-400'} text-center space-y-3`}>
              <Layout size={32} className="mx-auto opacity-20" />
              <p className="text-[10px] font-black uppercase tracking-widest">Mais automações em breve</p>
           </div>
        </div>

        {/* Visualização Real */}
        <div className="lg:col-span-8">
           <Card className="bg-white border border-gray-100 shadow-2xl rounded-[40px] overflow-hidden flex flex-col h-[700px]">
              <div className="p-6 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
                 <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-red-400"></div>
                    <div className="w-2 h-2 rounded-full bg-amber-400"></div>
                    <div className="w-2 h-2 rounded-full bg-green-400"></div>
                    <span className="ml-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Assunto: {activePreview === 'manager' ? 'Sua Unidade Logística está PRONTA!' : 'Seja Bem-vindo à Equipe!'}</span>
                 </div>
                 <button className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl text-[9px] font-black uppercase tracking-widest shadow-lg shadow-primary/20 hover:brightness-110 transition-all active:scale-95">
                    <Send size={12} /> Testar Disparo
                 </button>
              </div>
              <div className="flex-1 bg-slate-200 p-8 overflow-y-auto scrollbar-hide flex justify-center">
                 <div 
                    className={`bg-white shadow-2xl transition-all duration-500 overflow-hidden ${viewType === 'desktop' ? 'w-full max-w-[600px]' : 'w-[360px] rounded-[40px]'}`}
                    dangerouslySetInnerHTML={{ __html: html }}
                 />
              </div>
           </Card>
        </div>
      </div>
    </div>
  );
};

const TemplateCard = ({ active, onClick, icon: Icon, title, desc }: any) => (
  <button 
    onClick={onClick}
    className={`w-full p-6 rounded-[32px] border-2 transition-all text-left flex items-center gap-5 group ${
      active 
        ? 'bg-primary border-primary text-white shadow-xl shadow-primary/20 scale-[1.02]' 
        : 'bg-white border-gray-100 text-secondary hover:border-primary/30'
    }`}
  >
     <div className={`p-4 rounded-2xl shadow-inner transition-colors ${active ? 'bg-white/20' : 'bg-gray-50 text-gray-400 group-hover:text-primary'}`}>
        <Icon size={24} />
     </div>
     <div>
        <h4 className="font-black uppercase tracking-tight text-sm">{title}</h4>
        <p className={`text-[10px] font-bold mt-1 leading-relaxed ${active ? 'text-white/60' : 'text-gray-400'}`}>{desc}</p>
     </div>
  </button>
);
